import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of, switchMap, tap } from 'rxjs';
import {
  InterestPageFaq,
  InterestPageHeroOption,
  InterestPageSettings,
  InterestPartyItem,
} from '../models/trip-interest.types';
import { DataTransform, DateField } from '../utilities/dataTransformFunctions';
import { EnvService } from './env.service';
import { SessionService } from './session.service';
import { TripService } from './trip.service';
import { TripAccessService } from './trip-access/trip-access.service';

const interestPartyDateFields: DateField<InterestPartyItem>[] = [
  { fieldName: 'createdAt', dateType: 'local' },
  { fieldName: 'convertedAt', dateType: 'local' },
];

const heroOptionDateFields: DateField<InterestPageHeroOption>[] = [
  { fieldName: 'tripDate', dateType: 'local' },
];

@Injectable({ providedIn: 'root' })
export class TripInterestService {
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvService);
  private readonly tripService = inject(TripService);
  private readonly sessionService = inject(SessionService);
  private readonly tripAccess = inject(TripAccessService);

  private readonly access = computed(() => this.tripAccess.context(this.tripService.currentTrip.value()));
  /** Only the GL edits the interest page; anyone else would just get 403s. */
  private readonly canManagePage = computed(() => this.access()?.canAccessInterestPage ?? false);
  /** Any leader can add or dismiss interested parties (standard trips only). */
  private readonly canSeeParties = computed(() => this.access()?.canManageInterestedParties ?? false);
  /** Turning fundraising on unpublishes the page server-side; refetch settings when it flips. */
  private readonly tripPartDonate = computed(() => this.tripService.currentTrip.value()?.partDonate ?? 0);

  readonly settings = rxResource({
    params: () => ({
      tripId: this.tripService.currentTripId(),
      sessionReady: this.sessionService.sessionReady(),
      allowed: this.canManagePage(),
      partDonate: this.tripPartDonate(),
    }),
    stream: ({ params }) => {
      if (!params.sessionReady || params.tripId === null || !params.allowed) {
        return of(null as InterestPageSettings | null);
      }
      return this.http.get<InterestPageSettings>(
        `${this.envService.apiUrl}/trips/${params.tripId}/interest-page/settings`,
      );
    },
  });

  readonly heroOptions = rxResource({
    params: () => ({
      tripId: this.tripService.currentTripId(),
      sessionReady: this.sessionService.sessionReady(),
      allowed: this.canManagePage(),
    }),
    stream: ({ params }) => {
      if (!params.sessionReady || params.tripId === null || !params.allowed) {
        return of([] as InterestPageHeroOption[]);
      }
      return this.http
        .get<InterestPageHeroOption[]>(
          `${this.envService.apiUrl}/trips/${params.tripId}/interest-page/hero-options`,
        )
        .pipe(switchMap((rows) => DataTransform.replaceDatesArray(rows, heroOptionDateFields)));
    },
  });

  readonly parties = rxResource({
    params: () => ({
      tripId: this.tripService.currentTripId(),
      sessionReady: this.sessionService.sessionReady(),
      allowed: this.canSeeParties(),
    }),
    stream: ({ params }) => {
      if (!params.sessionReady || params.tripId === null || !params.allowed) {
        return of([] as InterestPartyItem[]);
      }
      return this.http
        .get<InterestPartyItem[]>(`${this.envService.apiUrl}/trips/${params.tripId}/interest`)
        .pipe(
          switchMap((rows) => DataTransform.replaceDatesArray(rows, interestPartyDateFields)),
        );
    },
  });

  saveContent(
    tripId: number,
    body: {
      glIntro: string;
      tripDescription: string;
      youtubeId: string;
      youtubeTitle: string;
      heroTripId: number | null;
      faqs: InterestPageFaq[];
    },
  ): Observable<InterestPageSettings> {
    return this.http
      .put<InterestPageSettings>(
        `${this.envService.apiUrl}/trips/${tripId}/interest-page/content`,
        body,
      )
      .pipe(tap(() => this.settings.reload()));
  }

  toggle(tripId: number, interestPage: 0 | 1): Observable<InterestPageSettings> {
    return this.http
      .put<InterestPageSettings>(
        `${this.envService.apiUrl}/trips/${tripId}/interest-page/toggle`,
        { interestPage },
      )
      .pipe(
        tap(() => {
          this.settings.reload();
          this.tripService.currentTrip.reload();
        }),
      );
  }

  /** Discards an interested party's request; they can express interest again later. */
  dismiss(tripId: number, interestId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.envService.apiUrl}/trips/${tripId}/interest/${interestId}`)
      .pipe(tap(() => this.parties.reload()));
  }

  convert(tripId: number, interestId: number): Observable<InterestPartyItem> {
    return this.http
      .post<InterestPartyItem>(
        `${this.envService.apiUrl}/trips/${tripId}/interest/${interestId}/convert`,
        {},
      )
      .pipe(
        tap(() => {
          this.parties.reload();
          this.tripService.currentTrip.reload();
        }),
      );
  }
}
